import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Cluster, Grid, Input, Select, Stack } from "@salesapp/ui";
import { TipHeading, TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

type MarketingPost = {
  post_id: string;
  platform: string;
  channel: string | null;
  post_type: string | null;
  status: string | null;
  published_at_local: string | null;
  permalink: string | null;
  destination_url: string | null;
  utm_campaign: string | null;
  caption_summary: string | null;
  creative_file: string | null;
  archive_folder: string | null;
  notes: string | null;
  metrics_source: string | null;
  metrics_as_of_utc: string | null;
  last_updated_utc: string | null;
  impressions_views: number | null;
  reach: number | null;
  clicks: number | null;
  link_clicks: number | null;
  engagements: number | null;
  likes_reactions: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  video_views: number | null;
  signups_attributed: number | null;
  trials_started: number | null;
};

interface MarketingSummary {
  post_count: number;
  platform_count: number;
  impressions_30d: number;
  clicks_30d: number;
  signups_30d: number;
  metrics_window: string;
  last_imported_at: string | null;
}

interface MarketingMetricsResponse {
  summary: MarketingSummary;
  posts: MarketingPost[];
  total: number;
  platforms: string[];
}

interface ImportResult {
  posts_new: number;
  posts_updated: number;
  daily_new: number;
  daily_updated: number;
  duplicates_merged: number;
  skipped: string[];
  skipped_count: number;
}

type ColumnKind = "text" | "choice" | "number" | "date" | "link";
type ColumnKey = keyof MarketingPost;

interface Column {
  key: ColumnKey;
  label: string;
  kind: ColumnKind;
}

/** Every imported field is a column: each one sorts, and each one has its own filter. */
const COLUMNS: Column[] = [
  { key: "published_at_local", label: "Published", kind: "date" },
  { key: "platform", label: "Platform", kind: "choice" },
  { key: "channel", label: "Channel", kind: "choice" },
  { key: "post_type", label: "Post type", kind: "choice" },
  { key: "status", label: "Status", kind: "choice" },
  { key: "post_id", label: "Post ID", kind: "text" },
  { key: "caption_summary", label: "Caption", kind: "text" },
  { key: "permalink", label: "Permalink", kind: "link" },
  { key: "destination_url", label: "Destination", kind: "link" },
  { key: "utm_campaign", label: "UTM campaign", kind: "text" },
  { key: "impressions_views", label: "Views", kind: "number" },
  { key: "reach", label: "Reach", kind: "number" },
  { key: "clicks", label: "Clicks", kind: "number" },
  { key: "link_clicks", label: "Link clicks", kind: "number" },
  { key: "engagements", label: "Engage.", kind: "number" },
  { key: "likes_reactions", label: "Likes", kind: "number" },
  { key: "comments", label: "Comments", kind: "number" },
  { key: "shares", label: "Shares", kind: "number" },
  { key: "saves", label: "Saves", kind: "number" },
  { key: "video_views", label: "Video views", kind: "number" },
  { key: "signups_attributed", label: "Signups", kind: "number" },
  { key: "trials_started", label: "Trials", kind: "number" },
  { key: "creative_file", label: "Creative", kind: "text" },
  { key: "archive_folder", label: "Archive folder", kind: "text" },
  { key: "metrics_source", label: "Metrics source", kind: "choice" },
  { key: "metrics_as_of_utc", label: "Metrics as of", kind: "date" },
  { key: "last_updated_utc", label: "Last updated", kind: "date" },
  { key: "notes", label: "Notes", kind: "text" },
];

function compactNumber(value: number | null): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact" }).format(value);
}

/**
 * Numeric filter: `100` (equals), `>100`, `>=100`, `<100`, `<=100` or `10-50` (inclusive range).
 * Unparseable input matches everything, so a half-typed filter never blanks the table.
 */
function matchesNumber(value: number | null, filter: string): boolean {
  const text = filter.trim();
  if (!text) return true;
  if (value == null) return false;
  const range = /^(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)$/.exec(text);
  if (range) return value >= Number(range[1]) && value <= Number(range[2]);
  const compare = /^(>=|<=|>|<|=)?\s*(-?\d+(?:\.\d+)?)$/.exec(text);
  if (!compare) return true;
  const target = Number(compare[2]);
  switch (compare[1]) {
    case ">":
      return value > target;
    case ">=":
      return value >= target;
    case "<":
      return value < target;
    case "<=":
      return value <= target;
    default:
      return value === target;
  }
}

function matchesFilter(post: MarketingPost, column: Column, filter: string): boolean {
  if (!filter.trim()) return true;
  const value = post[column.key];
  if (column.kind === "number") return matchesNumber(value as number | null, filter);
  if (column.kind === "choice") return (value ?? "") === filter;
  return String(value ?? "").toLowerCase().includes(filter.trim().toLowerCase());
}

function compareValues(a: MarketingPost, b: MarketingPost, column: Column, dir: "asc" | "desc"): number {
  const left = a[column.key];
  const right = b[column.key];
  // Empty cells sort last in either direction so they never crowd out real data.
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  let result: number;
  if (column.kind === "number") result = (left as number) - (right as number);
  else if (column.kind === "date") result = Date.parse(String(left)) - Date.parse(String(right));
  else result = String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true });
  return dir === "asc" ? result : -result;
}

function importSummary(result: ImportResult): string {
  const parts = [
    `${result.posts_new} new and ${result.posts_updated} updated posts`,
    `${result.daily_new} new and ${result.daily_updated} updated daily snapshots`,
  ];
  if (result.duplicates_merged > 0) parts.push(`${result.duplicates_merged} duplicate rows in the file merged`);
  return `Imported ${parts.join(", ")}.`;
}

export function MarketingMetricsPage() {
  const [data, setData] = useState<MarketingMetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [filters, setFilters] = useState<Partial<Record<ColumnKey, string>>>({});
  const [sortKey, setSortKey] = useState<ColumnKey>("published_at_local");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      try {
        const row = await api.get<MarketingMetricsResponse>("/operator/marketing-metrics?limit=5000");
        if (!cancelled) setData(row);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Marketing metrics could not be loaded.");
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const posts = data?.posts;
  const choices = useMemo(() => {
    const out: Partial<Record<ColumnKey, string[]>> = {};
    for (const column of COLUMNS) {
      if (column.kind !== "choice") continue;
      const values = new Set<string>();
      for (const post of posts ?? []) {
        const value = post[column.key];
        if (value) values.add(String(value));
      }
      out[column.key] = [...values].sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [posts]);

  const visible = useMemo(() => {
    const sortColumn = COLUMNS.find((column) => column.key === sortKey) ?? COLUMNS[0];
    const active = COLUMNS.filter((column) => (filters[column.key] ?? "").trim() !== "");
    return (posts ?? [])
      .filter((post) => active.every((column) => matchesFilter(post, column, filters[column.key] ?? "")))
      .sort((a, b) => compareValues(a, b, sortColumn, sortDir));
  }, [posts, filters, sortKey, sortDir]);

  function toggleSort(key: ColumnKey) {
    if (key === sortKey) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  }

  async function onFileChosen(file: File | undefined) {
    if (fileInput.current) fileInput.current.value = "";
    if (!file) return;
    setImportResult(null);
    setImportError(null);
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setImportError("Choose an Excel .xlsx file.");
      return;
    }
    setImporting(true);
    try {
      setImportResult(await api.upload<ImportResult>("/operator/marketing-metrics/import-xlsx", file));
      setReloadKey((key) => key + 1);
    } catch (err) {
      setImportError(err instanceof ApiError ? err.message : "The spreadsheet could not be imported.");
    } finally {
      setImporting(false);
    }
  }

  if (!data) {
    return error ? <p className="admin-error">{error}</p> : <p>Loading…</p>;
  }

  const { summary, total } = data;
  const filtering = Object.values(filters).some((value) => (value ?? "").trim() !== "");

  return (
    <Stack gap={5}>
      <Cluster gap={3} align="center" justify="between">
        <Cluster gap={3} align="center">
          <h2>Marketing metrics</h2>
          <p className="admin-muted">
            Ads and social post performance, imported from the marketing team's tracking sheet.
            Last imported {formatUtc(summary.last_imported_at)}.
          </p>
        </Cluster>
        <Cluster gap={2} align="center">
          <input
            ref={fileInput}
            type="file"
            hidden
            aria-label="Marketing spreadsheet file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => void onFileChosen(event.target.files?.[0])}
          />
          <Button disabled={importing} onClick={() => fileInput.current?.click()}>
            {importing ? "Importing…" : "Import spreadsheet"}
          </Button>
        </Cluster>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}
      {importError && <Flash tone="error">{importError}</Flash>}
      {importResult && (
        <Stack gap={2}>
          <Flash tone="success">{importSummary(importResult)}</Flash>
          {importResult.skipped_count > 0 && (
            <details>
              <summary className="admin-error">
                {importResult.skipped_count} rows were skipped and not imported
              </summary>
              <ul>
                {importResult.skipped.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </details>
          )}
        </Stack>
      )}

      <Grid columns={3} gap={4}>
        <Kpi
          label="Tracked posts"
          tip="Total posts recorded across every platform in the tracking sheet."
          value={summary.post_count}
          hint={`${summary.platform_count} platforms`}
        />
        <Kpi
          label="Impressions"
          tip={`Sum of impressions/views across tracked posts (${summary.metrics_window}).`}
          value={compactNumber(summary.impressions_30d)}
          hint={`${compactNumber(summary.clicks_30d)} clicks`}
        />
        <Kpi
          label="Signups attributed"
          tip={`Signups attributed to a tracked post, per the sheet's attribution column (${summary.metrics_window}).`}
          value={summary.signups_30d}
          hint={summary.metrics_window}
        />
      </Grid>

      <Card>
        <Stack gap={4}>
          <Cluster gap={3} align="center" justify="between">
            <TipHeading
              label="Posts"
              tip="One row per tracked post, unique by Post ID. Click a column heading to sort. Type in the box under a heading to filter that column; number columns accept 100, >100, <=50 or 10-50."
            />
            <Cluster gap={3} align="center">
              <p className="admin-muted">
                {visible.length} shown of {total}
              </p>
              {filtering && (
                <Button variant="secondary" size="sm" onClick={() => setFilters({})}>
                  Clear filters
                </Button>
              )}
            </Cluster>
          </Cluster>
          <div style={{ overflowX: "auto" }}>
            <table className="admin-marketing-table">
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      aria-sort={sortKey === column.key ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                    >
                      <button type="button" className="admin-sort-btn" onClick={() => toggleSort(column.key)}>
                        {column.label}
                      </button>
                    </th>
                  ))}
                </tr>
                <tr>
                  {COLUMNS.map((column) => (
                    <th key={column.key}>
                      {column.kind === "choice" ? (
                        <Select
                          aria-label={`Filter ${column.label}`}
                          value={filters[column.key] ?? ""}
                          onChange={(event) => setFilters((prev) => ({ ...prev, [column.key]: event.target.value }))}
                        >
                          <option value="">Any</option>
                          {(choices[column.key] ?? []).map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          aria-label={`Filter ${column.label}`}
                          placeholder={column.kind === "number" ? "e.g. >100" : "Filter"}
                          value={filters[column.key] ?? ""}
                          onChange={(event) => setFilters((prev) => ({ ...prev, [column.key]: event.target.value }))}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="admin-muted">
                      {total === 0 ? "No posts imported yet." : "No posts match the filters."}
                    </td>
                  </tr>
                )}
                {visible.map((post) => (
                  <tr key={post.post_id}>
                    {COLUMNS.map((column) => (
                      <td key={column.key} className={column.kind === "number" ? undefined : "admin-marketing-text"}>
                        <Cell post={post} column={column} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}

function Cell({ post, column }: { post: MarketingPost; column: Column }) {
  const value = post[column.key];
  if (value == null || value === "") return <span className="admin-muted">—</span>;
  if (column.kind === "number") return <>{compactNumber(value as number)}</>;
  if (column.kind === "date") return <>{formatUtc(String(value))}</>;
  if (column.kind === "link") {
    return (
      <a href={String(value)} target="_blank" rel="noreferrer" title={String(value)}>
        {String(value)}
      </a>
    );
  }
  if (column.key === "status") return <span className={`admin-badge is-${String(value)}`}>{String(value)}</span>;
  return <span title={String(value)}>{String(value)}</span>;
}

function Kpi({
  label,
  tip,
  value,
  hint,
}: {
  label: string;
  tip: string;
  value: number | string;
  hint: string;
}) {
  return (
    <Card>
      <Stack gap={2}>
        <TipKicker label={label} tip={tip} />
        <p className="admin-kpi">{value}</p>
        <p className="admin-muted">{hint}</p>
      </Stack>
    </Card>
  );
}
